/**
 * Sprint Redesign v1 (Do 03.09.2026) — Redesign-TopBar.
 *
 * Permanente Top-Bar oben, fixed-position, sichtbar auf allen Redesign-Pages.
 *
 * Elemente:
 *   Links:  Logo (klein, klickbar → /dashboard) + Hamburger (mobile only)
 *   Mittig: Brotkrumen (Dashboard > aktuelle Seite)
 *   Rechts: User-Avatar-Dropdown (Profil · Settings · Logout)
 *
 * Sprint-Backlog Redesign v1 · Item 3.
 */
import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, LogOut, UserCircle, ChevronDown, Menu, ArrowLeft, HelpCircle, Award } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ThemeToggle from "@/components/redesign/ThemeToggle";
import Glossar from "@/components/redesign/Glossar";
import TopBarFontScale from "@/components/redesign/TopBarFontScale";
import PionierBadge from "@/components/PionierBadge";
import PlanBadge from "@/components/PlanBadge";
import brandMark from "@/assets/brand/f4s-logo.png";
import { CockpitWord } from "@/lib/journeyFlags";

// Route-Titel für Brotkrumen
const ROUTE_TITLES = {
  "/dashboard": "Mein PocketCoach",
  "/mein-muster": "So kommuniziere ich",
  "/andere-verstehen": "Mein Gegenüber",
  "/wirkung-gestalten": "Kommunikation als Türöffner",
  "/videokurs": "Hintergrundwissen",
  "/anliegen": "Anliegen",
  "/profil": "Mein Profil",
};

// Sprint V26 (Fr 27.02.2026) — kompaktes Plan-Label fuer den Mobile-Dropdown.
// Spiegelt die gleiche Tier-Detektion wie PlanBadge, aber liefert die volle
// Bezeichnung („Free" / „Pro Solo · Monat" / „Pro Solo · Jahr" / „PowerPaket").
function planLabel(user) {
  if (!user) return "Free";
  const raw = String(user.license_tier || user.tier || "").toLowerCase().trim();
  if (raw.startsWith("pro_solo_year") || raw.includes("yearly")) return "Pro Solo · Jahr";
  if (raw.startsWith("pro_solo_month") || raw.includes("monthly")) return "Pro Solo · Monat";
  if (raw === "powerpaket" || user.powerpaket_lifetime === true) return "PowerPaket";
  return "Free";
}

function useCurrentUser() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchUser = () => {
      api.get("/user/dashboard").then(
        ({ data }) => { if (!cancelled) setUser(data?.user || null); },
        () => { if (!cancelled) setUser(null); },
      );
    };
    fetchUser();
    // Sprint V32 — bei Avatar-Upload/Delete refetchen
    const handler = () => fetchUser();
    window.addEventListener("f4s:user-avatar-changed", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("f4s:user-avatar-changed", handler);
    };
  }, []);
  return user;
}

// Sprint V32 — Avatar-Blob-Fetch fuer TopBar (img-Tag kann keine Auth-Header senden)
function useAvatarBlob(avatarUrl) {
  const [blob, setBlob] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    if (!avatarUrl) { setBlob(null); return; }
    const relPath = avatarUrl.replace(/^\/api\//, "");
    api.get(relPath, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlob(objectUrl);
      })
      .catch(() => { if (alive) setBlob(null); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [avatarUrl]);
  return blob;
}

export default function RedesignTopBar({ onMobileMenuOpen = () => {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const avatarBlob = useAvatarBlob(user?.avatar_url);
  // Post-Launch-H8 (10.02.2026) — Logout via AuthContext, damit React-State
  // (useAuth) synchron mit den Backend-Cookies geleert wird. Sonst Loop.
  const { logout: authLogout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [glossarOpen, setGlossarOpen] = useState(false);
  const dropdownRef = useRef(null);

  // POSTLAUNCH-V4 — Glossar-Inline-Links feuern Custom-Event, wir öffnen Modal via Prop.
  useEffect(() => {
    const handler = () => setGlossarOpen(true);
    window.addEventListener("f4s:glossar-open", handler);
    return () => window.removeEventListener("f4s:glossar-open", handler);
  }, []);

  const currentTitle = ROUTE_TITLES[location.pathname] || null;
  const isDashboard = location.pathname === "/dashboard";

  // Click-outside handler
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  const handleLogout = async () => {
    // Post-Launch-H8 (10.02.2026) — Logout-Loop-Bug (Boardroom-CoS):
    // Vorher: nur api.post("/auth/logout") ohne setUser(false) → useAuth-Context
    // blieb stale mit altem User → Login.jsx redirected zurueck auf /cockpit → Loop.
    // Fix: AuthContext.logout() clear'd sowohl Backend-Cookies als auch React-State,
    // dann Redirect zu /login?logout=1 mit Erfolgsbanner.
    try {
      await authLogout();
    } catch (_) { /* ignore */ }
    navigate("/login?logout=1", { replace: true });
  };

  const initials = (user?.first_name || user?.email || "?")
    .split(/[\s@]+/)[0]
    .slice(0, 2)
    .toUpperCase();

  return (
    <header
      className="sticky top-0 z-40 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 h-14 flex items-center px-2 sm:px-4 lg:px-6 flex-shrink-0"
      data-testid="redesign-topbar"
    >
      {/* Mobile: Hamburger (I27) */}
      <button
        type="button"
        onClick={onMobileMenuOpen}
        data-testid="topbar-mobile-menu-btn"
        aria-label="Menü öffnen"
        className="lg:hidden mr-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 p-2"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile: Back-Button (I28) — nur wenn nicht Dashboard */}
      {!isDashboard && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="topbar-mobile-back"
          aria-label="Zurück"
          className="lg:hidden mr-1 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      )}

      {/* Mobile: Compact Brand-Mark — flex-shrink-0 + object-contain
          verhindert horizontales Quetschen bei engem TopBar-Slot
          (Sprint V26, Fr 27.02.2026 — Andreas: „Logo verzerrt"). */}
      <Link
        to="/dashboard"
        data-testid="topbar-logo-mobile"
        className="lg:hidden flex items-center gap-2 flex-shrink-0"
      >
        <img
          src={brandMark}
          alt="Flex4Success"
          data-testid="topbar-brand-logo"
          className="h-8 w-auto object-contain flex-shrink-0"
        />
      </Link>

      {/* Desktop: KEIN Logo (Sidebar hat das Branding). Brotkrumen direkt links. */}

      {/* Desktop: Brotkrumen */}
      <nav
        className="hidden lg:flex items-center gap-1 text-sm"
        aria-label="Brotkrumen"
        data-testid="topbar-breadcrumbs"
      >
        <Link
          to="/dashboard"
          className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white font-semibold"
        >
          Mein PocketCoach
        </Link>
        {!isDashboard && currentTitle && (
          <>
            <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500" aria-hidden="true" />
            <span className="text-slate-900 dark:text-white font-bold" data-testid="topbar-current-page">
              {currentTitle}
            </span>
          </>
        )}
      </nav>

      {/* Rechts: Pionier-Badge (falls Pionier) + Glossar + Theme-Toggle + User Menu */}
      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        {/* Post-Launch-H6 (10.02.2026) — Boardroom-Directive:
            Pionier-Kennzeichnung global auf allen Redesign-Seiten sichtbar
            (Free-Anliegen-Flow, Profil, Muster, Gegenüber, Türöffner, Videokurs).
            Rendert nur wenn user.is_pionier === true.
            Hotfix H6.1 (10.02.2026) — Andreas: Badge war auf Mobile via
            `hidden sm:block` unsichtbar. Jetzt IMMER sichtbar (auch iPhone),
            weil der Badge kompakt bleibt (~90px). Kein Platz-Konflikt mit
            Avatar-Dropdown, weil Font-Scale + Glossar-Icon ohnehin auf
            Mobile ausgeblendet werden. */}
        <PionierBadge user={user} />
        {/* Sprint-V24 Issue K (11.02.2026, Boardroom-CoS) — Plan-Status
            permanent oben. Rendert Free / Pro Solo Monat / Pro Solo Jahr /
            PowerPaket als kleines Pill mit Farb-Semantik pro Tier. Klick
            fuehrt zu /pricing (Upgrade / Verwaltung). */}
        <PlanBadge user={user} onClick={() => navigate("/pricing")} />
        <button
          type="button"
          onClick={() => setGlossarOpen(true)}
          data-testid="topbar-glossar-btn"
          aria-label="Glossar öffnen"
          title="Glossar: Anliegen · Gegenüber · Türöffner"
          className="hidden sm:inline-flex rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 p-2 transition-colors"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
        <TopBarFontScale />
        <ThemeToggle />
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen((v) => !v)}
            data-testid="topbar-user-menu-trigger"
            aria-haspopup="menu"
            aria-expanded={dropdownOpen}
            className="flex items-center gap-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 py-1 pl-1 pr-2 transition-colors"
          >
            <div
              className="w-8 h-8 rounded-full bg-[var(--fts-primary-bright)] text-white flex items-center justify-center font-bold text-xs overflow-hidden"
              data-testid="topbar-user-avatar"
              data-has-image={avatarBlob ? "true" : "false"}
            >
              {avatarBlob ? (
                <img src={avatarBlob} alt="Profilbild" className="w-full h-full object-cover" data-testid="topbar-user-avatar-img" />
              ) : (
                initials
              )}
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" aria-hidden="true" />
          </button>

          {dropdownOpen && (
            <div
              role="menu"
              data-testid="topbar-user-menu"
              className="absolute right-0 top-full mt-1 w-56 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg py-1.5 z-50"
            >
              {user?.first_name && (
                <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800">
                  <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user.first_name}</p>
                  {user?.email && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                  )}
                  {/* Sprint V26 (Fr 27.02.2026) — Auf Mobile werden Pionier +
                      PlanBadge in der TopBar ausgeblendet, damit das Logo
                      nicht mehr gequetscht wird. Hier im Dropdown-Header
                      erscheinen die beiden Statuselemente nur auf Mobile
                      (Desktop hat sie bereits in der Bar). */}
                  <div
                    className="sm:hidden mt-2 flex flex-wrap items-center gap-2"
                    data-testid="topbar-menu-status-mobile"
                  >
                    {user?.is_pionier && (
                      <span
                        data-testid="topbar-menu-pionier-mobile"
                        className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm"
                      >
                        <Award className="w-3 h-3" aria-hidden="true" />
                        Pionier
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => { setDropdownOpen(false); navigate("/pricing"); }}
                      data-testid="topbar-menu-plan-mobile"
                      className="inline-flex items-center rounded-full border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-800 dark:text-indigo-200 hover:opacity-80"
                    >
                      Plan: {planLabel(user)}
                    </button>
                  </div>
                </div>
              )}
              <Link
                to="/profil"
                onClick={() => setDropdownOpen(false)}
                data-testid="topbar-menu-profil"
                role="menuitem"
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <UserCircle className="w-4 h-4" />
                Mein Profil
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                data-testid="topbar-menu-logout"
                role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-left"
              >
                <LogOut className="w-4 h-4" />
                Ausloggen
              </button>
            </div>
          )}
        </div>
      </div>

      <Glossar open={glossarOpen} onClose={() => setGlossarOpen(false)} />
    </header>
  );
}
